// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2022 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import StepperContextProvider from "@gcg/App/Widgets/CheckoutStepper/StepperContext";
import Zone from "@insite/client-framework/Components/Zone";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getReviewAndPayCartState } from "@insite/client-framework/Store/Data/Carts/CartsSelector";
import { getCartState, getCurrentCartState } from "@insite/client-framework/Store/Data/Carts/CartsSelector";
import loadCurrentCart from "@insite/client-framework/Store/Data/Carts/Handlers/LoadCurrentCart";
import { getCurrentCountries } from "@insite/client-framework/Store/Data/Countries/CountriesSelectors";
import loadCurrentCountries from "@insite/client-framework/Store/Data/Countries/Handlers/LoadCurrentCountries";
import { getQueryStrings } from "@insite/client-framework/Store/Data/Pages/PageSelectors";
import loadCurrentPromotions from "@insite/client-framework/Store/Data/Promotions/Handlers/LoadCurrentPromotions";
import { getReviewAndPayPromotions } from "@insite/client-framework/Store/Data/Promotions/PromotionsSelectors";
import clearMessages from "@insite/client-framework/Store/Pages/CheckoutReviewAndSubmit/Handlers/ClearMessages";
import loadDataIfNeeded from "@insite/client-framework/Store/Pages/CheckoutReviewAndSubmit/Handlers/LoadDataIfNeeded";
import notifyProductRecs from "@insite/client-framework/Store/Pages/CheckoutReviewAndSubmit/Handlers/NotifyProductRecs";
import setCartId from "@insite/client-framework/Store/Pages/CheckoutReviewAndSubmit/Handlers/SetCartId";
import setPlaceOrderErrorMessage from "@insite/client-framework/Store/Pages/CheckoutReviewAndSubmit/Handlers/SetPlaceOrderErrorMessage";
import PageModule from "@insite/client-framework/Types/PageModule";
import PageProps from "@insite/client-framework/Types/PageProps";
import AddressErrorModal from "@insite/content-library/Components/AddressErrorModal";
import Page from "@insite/mobius/Page";
import React, { useEffect } from "react";
import { connect, ResolveThunks } from "react-redux";

const mapDispatchToProps = {
    setCartId,
    loadCurrentCart,
    loadCurrentPromotions,
    loadCurrentCountries,
    setPlaceOrderErrorMessage,
    loadDataIfNeeded,
    clearMessages,
    notifyProductRecs,
};

const mapStateToProps = (state: ApplicationState) => {
    const { cartId } = getQueryStrings(state);
    const cartState = getReviewAndPayCartState(state);
    const promotionsDataView = getReviewAndPayPromotions(state);

    const isLoadDataNeeded =
    ((!cartState.value || (cartState.value.lineCount && !cartState.value.cartLines) || !cartState.value.carriers) &&
        !cartState.isLoading) ||
    (!promotionsDataView.value && !promotionsDataView.isLoading);

    return {
        cartId,
        isLoadDataNeeded: isLoadDataNeeded || !getCurrentCountries(state),
        productRecsCalled: state.pages.checkoutReviewAndSubmit.productRecsCalled,
    };
};

type Props = PageProps & ResolveThunks<typeof mapDispatchToProps> & ReturnType<typeof mapStateToProps>;

const CheckoutReviewAndSubmitPage = ({
    id,
    cartId,
    isLoadDataNeeded,
    productRecsCalled,
    setCartId,
    loadDataIfNeeded,
    setPlaceOrderErrorMessage,
    clearMessages,
    notifyProductRecs,
}: Props) => {
    useEffect(() => {
        if (isLoadDataNeeded) {
            loadDataIfNeeded({ cartId, cartLinesShouldBeExpanded: true });
        } else if (!productRecsCalled) {
            notifyProductRecs({ cartId });
        }
    });

    useEffect(() => {
        setCartId({ cartId });
        setPlaceOrderErrorMessage({});
        clearMessages();
    }, []);

    return (
        <StepperContextProvider initialStep={2}>
            <Page data-test-selector="checkoutReviewAndSubmitPage">
                <Zone zoneName="Content" contentId={id} />
                <AddressErrorModal />
            </Page>
        </StepperContextProvider>
    );
}

const pageModule: PageModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(CheckoutReviewAndSubmitPage),
    definition: {
        hasEditableUrlSegment: true,
        hasEditableTitle: true,
        pageType: "System",
    },
};

export default pageModule;

/**
 * @deprecated Use string literal "CheckoutReviewAndSubmitPage" instead of this constant.
 */
export const CheckoutReviewAndSubmitPageContext = "CheckoutReviewAndSubmitPage";
